import { getQueryClientInstance } from '@/api/queries/queryClient';
import { useAuthContext } from '@/context/AuthContext';
import { buildLocaleOverrideHeaderValue } from '@/dev/CountryOverrideDevTools/buildLocaleOverrideHeaderValue';
import useCountryOverrideStore from '@/dev/CountryOverrideDevTools/useCountryOverrideStore';
import { cubeQueryExtractor } from '@/dev/services/CubeQueryExtractor';
import getEnvVar from '@/utils/getEnvVar';
import cubejs, { HttpTransport } from '@cubejs-client/core';
import { CubeProvider } from '@cubejs-client/react';
import { lazy, ReactNode, Suspense, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const CubeQueryDevTools = lazy(() => import('@/dev/CubeQueryDevTools/CubeQueryDevTools'));

const apiUrl = getEnvVar('VITE_CUBE_JS_API_URL') ?? '';

/**
 * Returns the full pathname as the page context identifier for Cube.js query correlation.
 * The full path (including dynamic segments like country codes, UCIs, trial IDs) is
 * preserved in logs for maximum debugging value. Aggregation by page section can be
 * done at query time in CloudWatch Insights using parse/regex.
 *
 * Examples:
 *   /channel-focus/email              → "channel-focus/email"
 *   /hcp-overview/DE/12345            → "hcp-overview/DE/12345"
 *   /clinical-trial-id/NCT0123456789  → "clinical-trial-id/NCT0123456789"
 *   /                                 → "root"
 */
export const getPageContext = (pathname: string): string => {
  const trimmed = pathname.replace(/^\/+|\/+$/g, '');
  return trimmed || 'root';
};

/**
 * Returns the query string (without the leading '?') as a sub-context
 * identifier for Cube.js query correlation.
 *
 * Examples:
 *   "?section=engagement"          → "section=engagement"
 *   "?page=0&size=25"              → "page=0&size=25"
 *   ""                             → ""
 */
export const getPageSubContext = (search: string): string => search.replace(/^\?/, '');

export const buildCubeHeaders = ({
  pageContext,
  pageSubContext,
  localeOverride,
}: {
  pageContext: string;
  pageSubContext: string;
  localeOverride?: string;
}): Record<string, string> => ({
  'X-Page-Context': pageContext,
  ...(pageSubContext && { 'X-Page-Sub-Context': pageSubContext }),
  ...(localeOverride && { 'x-locale-override': localeOverride }),
});

const WithCubeProvider = ({ children }: { children: ReactNode }) => {
  const { token } = useAuthContext();
  const { pathname, search } = useLocation();
  const pageContext = getPageContext(pathname);
  const pageSubContext = getPageSubContext(search);
  const overrideCountries = useCountryOverrideStore(state => state.countries);
  const localeOverride = buildLocaleOverrideHeaderValue(overrideCountries);

  const cubejsApi = useMemo(
    () =>
      cubejs({
        apiUrl,
        transport: new HttpTransport({
          apiUrl,
          authorization: token,
          method: 'POST',
          headers: buildCubeHeaders({ pageContext, pageSubContext, localeOverride }),
        }),
      }),
    [token, pageContext, pageSubContext, localeOverride],
  );

  const interceptedApiRef = useRef<typeof cubejsApi | null>(null);
  if (interceptedApiRef.current !== cubejsApi) {
    cubeQueryExtractor.setCubejsApi(cubejsApi);
    cubeQueryExtractor.setQueryClient(getQueryClientInstance());
    interceptedApiRef.current = cubejsApi;
  }

  return (
    <CubeProvider cubejsApi={cubejsApi}>
      {children}
      <Suspense fallback={null}>
        <CubeQueryDevTools />
      </Suspense>
    </CubeProvider>
  );
};

export default WithCubeProvider;
